import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'

// Polished analytics panel: donut with total in the centre + a breakdown
// list (count and share per segment). Shared by Trainer/Student analytics.

interface Segment {
  label: string
  value: number
  color: string
}

interface Props {
  series: Segment[]
  /** Label shown under the total in the donut centre, e.g. "Trainers". */
  totalLabel?: string
}

export function AnalyticsBreakdown({ series, totalLabel = 'Total' }: Props) {
  const total = series.reduce((sum, s) => sum + s.value, 0)
  const labels = series.map((s) => s.label)
  const colors = series.map((s) => s.color)

  const options: ApexOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels,
    colors,
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { colors: ['#fff'], width: 2 },
    tooltip: {
      fillSeriesColor: false,
      y: { formatter: (v: number) => Number(v).toLocaleString() },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: { offsetY: 22, color: '#637381', fontSize: '13px' },
            value: { offsetY: -18, color: '#1C252E', fontSize: '24px', fontWeight: 700, formatter: (v) => Number(v).toLocaleString() },
            total: {
              show: true,
              label: totalLabel,
              color: '#637381',
              fontSize: '13px',
              formatter: () => Number(total).toLocaleString(),
            },
          },
        },
      },
    },
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
      <div className="w-full max-w-[240px] shrink-0">
        <ReactApexChart type="donut" series={series.map((s) => s.value)} options={options} height={240} width="100%" />
      </div>

      <ul className="w-full flex-1 space-y-3">
        {series.map((s) => {
          const pct = total > 0 ? (s.value / total) * 100 : 0
          return (
            <li key={s.label}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {Number(s.value).toLocaleString()}
                  <span className="ml-1.5 text-xs font-normal text-gray-400">{pct.toFixed(0)}%</span>
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: s.color }} />
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
